import { useForm } from '@tanstack/react-form';
import { memo } from 'react';
import z from 'zod';

import { $$toast } from '@doger/ui/components/ui/toast';

import { useTranslation } from '../../../../shared/i18n';
import { useRenameChatMutation } from './use-rename-chat.mutation';
import styles from './rename-chat.module.scss';

export type RenamingChatProps = {
  chatId: string;
  currentTitle: string;

  onSuccess: () => void;
  onCancel: () => void;
};

export const RenamingChat = memo(
  ({ chatId, currentTitle, onSuccess, onCancel }: RenamingChatProps) => {
    const { t } = useTranslation('app');
    const renameChatMutation = useRenameChatMutation({ chatId });

    const RenameChatSchema = z.object({
      title: z
        .string()
        .trim()
        .min(1, t('validation.title.required'))
        .max(255, t('validation.title.tooLong')),
    });

    const form = useForm({
      defaultValues: {
        title: currentTitle,
      },
      validators: {
        onSubmit: RenameChatSchema,
        onChange: RenameChatSchema,
        onMount: RenameChatSchema,
      },
      onSubmit: ({ value }) => {
        renameChatMutation.mutate({
          chatId,
          newTitle: value.title.trim(),
        });

        onSuccess();
      },
    });

    return (
      <form
        name='rename-chat-forms'
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field
          name='title'
          children={(field) => (
            <input
              className={styles.input}
              type='text'
              value={field.state.value}
              onChange={(e) => {
                const value = e.target.value;

                if (value.length > 255) {
                  $$toast.warning(
                    t('validation.title.truncated', { maxLength: '255' })
                  );
                }

                field.handleChange(value.slice(0, 255));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  form.handleSubmit();
                } else if (e.key === 'Escape') {
                  onCancel();
                }
              }}
              onBlur={() => {
                field.handleBlur();
                onCancel();
              }}
              autoFocus
            />
          )}
        />
      </form>
    );
  }
);
